import chroma from 'chroma-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './color-scale.module.css';

export interface ColorScaleProps {
  mobile?: boolean;
  min: number;
  max: number;
  hetero?: number;
  colorGradient: string[];
  /** Absolute data values for each color stop (NDVI multi-stop ramp). */
  stops?: number[];
}

function formatScaleValue(value: number): string {
  if (value === 0) {
    return '0';
  }
  if (Math.abs(value) < 1) {
    return value.toFixed(2);
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(2);
}

function buildScaleValues(min: number, max: number, stops?: number[]): number[] {
  if (stops?.length && stops.length >= 2) {
    const last = stops.length - 1;
    return [
      stops[0],
      stops[Math.floor(last * 0.25)],
      stops[Math.floor(last * 0.5)],
      stops[Math.floor(last * 0.75)],
      stops[last],
    ];
  }
  if (min === -1 && max === 0) {
    return [min, (min * 3) / 4, min / 2, min / 4, max];
  }
  return [min, max / 4, max / 2, (max * 3) / 4, max];
}

const SCALE_HEIGHT = 110;
const SCALE_WIDTH = 25;

function drawColorScale(
  ctx: CanvasRenderingContext2D,
  height: number,
  width: number,
  min: number,
  max: number,
  colorGradient: string[],
  stops?: number[],
) {
  const scaleBuilder = chroma.scale(colorGradient).mode(stops ? 'rgb' : 'lab');
  const scale = (
    stops?.length
      ? scaleBuilder.domain(stops)
      : scaleBuilder.domain([min, max])
  ).colors(500);
  const scaleReverse = scale.reverse();
  scaleReverse.forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, i * (height / 500), width, height / 500);
  });
}

export function ColorScale(props: ColorScaleProps) {
  const { t } = useTranslation(['map', 'plot'], { useSuspense: false });
  const canvasRefMobile = useRef<HTMLCanvasElement>(null);
  const canvasRefDesktop = useRef<HTMLCanvasElement>(null);
  const min = Number(props.min);
  const max = Number(props.max);

  useEffect(() => {
    const ctxMobile = canvasRefMobile.current?.getContext('2d');
    if (ctxMobile) {
      drawColorScale(
        ctxMobile,
        SCALE_HEIGHT,
        SCALE_WIDTH,
        min,
        max,
        props.colorGradient,
        props.stops,
      );
    }
  }, [props.colorGradient, props.stops, min, max]);

  useEffect(() => {
    const ctxDesktop = canvasRefDesktop.current?.getContext('2d');
    if (ctxDesktop) {
      drawColorScale(
        ctxDesktop,
        SCALE_HEIGHT,
        SCALE_WIDTH,
        min,
        max,
        props.colorGradient,
        props.stops,
      );
    }
  }, [props.colorGradient, props.stops, min, max]);

  const scaleValues = buildScaleValues(min, max, props.stops);
  const reversedScaleValues = [...scaleValues].reverse();

  if (props.mobile) {
    return (
      <div className={styles.container}>
        <div className={styles['title-echelle-mobile']}>
          {t('map:satellite_map')}
        </div>

        {props.hetero != null ? (
          <div className={styles['hetero-mobile']}>
            <strong>{t('plot:heterogeneity_index')}</strong> : {props.hetero}
          </div>
        ) : null}

        <div className={styles['echelle-mobile']}>
          <div className={styles['scale-values-mobile']}>
            {reversedScaleValues.map((value, index) => (
              <div key={index}>{formatScaleValue(value)}</div>
            ))}
          </div>

          <div className={styles['color-mobile']}>
            <canvas
              ref={canvasRefMobile}
              height={SCALE_HEIGHT}
              width={SCALE_WIDTH}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>{t('map:satellite_map')}</div>

      {props.hetero != null ? (
        <div className={styles.hetero}>
          <strong>{t('plot:heterogeneity_index')}</strong> : {props.hetero}
        </div>
      ) : null}

      <div className={styles.scaleRow}>
        <div className={styles.scaleValues}>
          {reversedScaleValues.map((value, index) => (
            <div key={index}>{formatScaleValue(value)}</div>
          ))}
        </div>

        <div className={styles.scaleBar}>
          <canvas
            ref={canvasRefDesktop}
            height={SCALE_HEIGHT}
            width={SCALE_WIDTH}
          />
        </div>
      </div>
    </div>
  );
}

export default ColorScale;
